import React from "react";
import styled from "styled-components";
import { formatCpf } from "@brazilian-utils/brazilian-utils";
import concedidoPqn from "../../assets/concedido-pequeno.png";
import {
  getPeriodoInicio,
  getPeriodoTermino,
} from "./VagaResumoCard";

const DIAS_SEMANA = [
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
  "Domingo",
];

const StatusBar = styled.div`
  font-family: "Public Sans", sans-serif;
  border-top: 1px solid #ccc;
  background-color: #f2f0f0;
  display: flex;
  align-items: center;
  padding: 12px 40px;
  gap: 20px;
`;

const SpecialtyName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const StatusBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #555555;
  font-weight: 500;

  img {
    width: 28px;
    height: 28px;
  }

  em {
    font-weight: 700;
    color: #2e7d32;
    font-style: italic;
  }
`;

const FormWrapper = styled.div`
  font-family: "Public Sans", sans-serif;
  padding: 24px 30px 40px 40px;
  max-width: 860px;
`;

const FormHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #ff9f1f;
  margin-bottom: 18px;
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 14px;
`;

const FieldGroup = styled.div`
  margin-bottom: 8px;
`;

const FormLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333333;
  margin-bottom: 4px;
`;

const FieldValue = styled.div`
  font-size: 15px;
  color: #555555;
  min-height: 24px;
`;

const DaysWrapper = styled.div`
  margin: 14px 0 18px;
`;

const DaysList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 6px;
`;

const DayLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: #555555;
  user-select: none;

  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid #4a4a4a;
    border-radius: 2px;
    background: #ffffff;
    position: relative;
    margin: 0;
  }

  input[type="checkbox"]:checked {
    background: #6ab8a8;
    border-color: #4a4a4a;
  }

  input[type="checkbox"]:checked::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid #000000;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  input[type="checkbox"]:disabled {
    opacity: 1;
  }
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
`;

const ActionButton = styled.button`
  border: none;
  padding: 10px 26px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
`;

const VoltarButton = styled(ActionButton)`
  background: #999;
  color: #ffffff;

  &:hover {
    background: #888;
  }

  &:disabled {
    background: #bbb;
    cursor: not-allowed;
  }
`;

const SalvarButton = styled(ActionButton)`
  background: #ff9f1f;
  color: #ffffff;

  &:hover {
    background: #e89010;
  }

  &:disabled {
    background: #f5c882;
    cursor: not-allowed;
  }
`;

const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString("pt-BR");
};

const maskCpf = (cpf) => {
  const digits = String(cpf || "").replace(/\D/g, "");
  if (digits.length !== 11) return "XXX.XXX.XXX-XX";
  return formatCpf(digits);
};

const CadastrarAlunoRevisao = ({
  solicitacao,
  formData,
  saving,
  onVoltar,
  onSalvar,
}) => {
  const especialidade =
    solicitacao?.vaga?.unidadeAssistencial?.especialidade?.nome || "Vaga";
  const localRealizacao = solicitacao?.vaga?.estabelecimentoSaude?.nome || "-";
  const inicioEstagio = getPeriodoInicio(solicitacao?.periodos) || "-";
  const terminoEstagio = getPeriodoTermino(solicitacao?.periodos) || "-";
  const cargaHorariaTotal = solicitacao?.horasTotaisPorAluno ?? "-";

  return (
    <>
      <StatusBar>
        <SpecialtyName>{especialidade}</SpecialtyName>
        <StatusBlock>
          <img src={concedidoPqn} alt="Concedido" />
          <span>
            Vaga <em>concedida</em> pelo NEPS
          </span>
        </StatusBlock>
      </StatusBar>

      <FormWrapper>
        <FormHeader>Confira o cadastro do Aluno</FormHeader>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel>Nome do aluno*</FormLabel>
            <FieldValue>{formData.nome}</FieldValue>
          </FieldGroup>
          <FieldGroup>
            <FormLabel>Data de Nascimento*</FormLabel>
            <FieldValue>{formatDate(formData.dataNascimento)}</FieldValue>
          </FieldGroup>
        </TwoColumnRow>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel>CPF do aluno*</FormLabel>
            <FieldValue>{maskCpf(formData.cpf)}</FieldValue>
          </FieldGroup>
          <FieldGroup>
            <FormLabel>Local de realização do estágio*</FormLabel>
            <FieldValue>{localRealizacao}</FieldValue>
          </FieldGroup>
        </TwoColumnRow>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel>Número de matrícula*</FormLabel>
            <FieldValue>{formData.numeroMatricula}</FieldValue>
          </FieldGroup>
          <FieldGroup>
            <FormLabel>Término do estágio*</FormLabel>
            <FieldValue>{terminoEstagio}</FieldValue>
          </FieldGroup>
        </TwoColumnRow>

        <FieldGroup>
          <FormLabel>Início do estágio*</FormLabel>
          <FieldValue>{inicioEstagio}</FieldValue>
        </FieldGroup>

        <DaysWrapper>
          <FormLabel>Dias da semana</FormLabel>
          <DaysList>
            {DIAS_SEMANA.map((dia) => (
              <DayLabel key={dia}>
                <input
                  type="checkbox"
                  checked={formData.diasSemana.includes(dia)}
                  disabled
                  readOnly
                />
                {dia}
              </DayLabel>
            ))}
          </DaysList>
        </DaysWrapper>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel>Horário de realização do estágio*</FormLabel>
            <FieldValue>{formData.horarioRealizacao}</FieldValue>
          </FieldGroup>
          <FieldGroup>
            <FormLabel>Carga horária total do estágio*</FormLabel>
            <FieldValue>{cargaHorariaTotal}h</FieldValue>
          </FieldGroup>
        </TwoColumnRow>

        <FormFooter>
          <VoltarButton type="button" onClick={onVoltar} disabled={saving}>
            Voltar
          </VoltarButton>
          <SalvarButton type="button" onClick={onSalvar} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </SalvarButton>
        </FormFooter>
      </FormWrapper>
    </>
  );
};

export default CadastrarAlunoRevisao;
